import React, { useState, useEffect, useRef } from "react";
import {
  Timeline,
  Card,
  Empty,
  Spin,
  Calendar,
  Badge,
  Typography,
  Button,
  message,
  Collapse,
  Space,
  Tooltip,
  FormInstance,
  Modal,
} from "antd";
import {
  InfoCircleOutlined,
  PlusOutlined,
  DownOutlined,
  UpOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { trackFeatureUsage } from "../../../lib/posthog";
import {
  TariffTimelineTabProps,
  Tariff,
  TariffGroup,
  TariffSubGroup,
  TariffFormValues,
} from "./utils/types";
import {
  getTariffColor,
  getTransportTypeColor,
  getTransportTypeEmoji,
  getTariffStatus,
} from "./utils/tariffHelpers";
import TariffFilterForm, { TariffFilterValues } from "./forms/TariffFilterForm";
import StatusTag from "./components/StatusTag";
import TariffForm from "./forms/TariffForm";
import TariffSuggestions from "./components/TariffSuggestions";
import {
  generateTariffSuggestions,
  getTopSuggestions,
} from "./utils/tariffSuggestions";
import { generateShortTariffCalendarTitle } from "../../../utils/calendarUtils";

const { Text } = Typography;
const { Panel } = Collapse;

const TariffTimelineTab: React.FC<TariffTimelineTabProps> = ({
  tariffs,
  transportTypes,
  originPorts,
  destinationPorts,
  tariffBrackets,
  containerSizes,
  bookingTypes,
  loading,
  fetchTariffs = () => {},
}) => {
  // State for filter values
  const [filterValues, setFilterValues] = useState<TariffFilterValues>({
    transportType: null,
    originPort: null,
    destinationPort: null,
    bookingType: null,
    containerSize: null,
    dateRange: [dayjs().startOf("day"), null],
    viewMode: "timeline",
    showActiveTariffs: true,
  });

  // State for add new tariff modal
  const [addTariffModalVisible, setAddTariffModalVisible] = useState(false);
  const [selectedGroupForAdd, setSelectedGroupForAdd] = useState<{
    transportType?: number;
    originPort?: number;
    destinationPort?: number;
    bookingType?: number;
    containerSize?: number;
    tariffBracket?: number;
  } | null>(null);

  // State to track which bracket groups have expanded expired tariffs
  const [expandedExpiredGroups, setExpandedExpiredGroups] = useState<
    Set<string>
  >(new Set());

  // Call fetchTariffs with initial filter values on component mount
  useEffect(() => {
    if (fetchTariffs) {
      fetchTariffs({
        showActiveTariffs: true,
        dateRange: [dayjs().startOf("day"), null],
      });
    }
    trackFeatureUsage("tariff_timeline_view");
  }, []); // Empty dependency array to ensure it only runs once on mount

  // Handle filter changes
  const handleFilterChange = (values: Partial<TariffFilterValues>) => {
    const updatedFilterValues = { ...filterValues, ...values };
    setFilterValues(updatedFilterValues);

    // Call the fetchTariffs function with the updated filters
    if (fetchTariffs) {
      fetchTariffs(updatedFilterValues);
    }

    trackFeatureUsage(`tariff_timeline_filter_changed`);
  };

  // Handler for adding a tariff from the timeline
  const handleAddTariffFromTimeline = (
    group: TariffGroup,
    bookingType?: number,
    containerSize?: number,
    tariffBracket?: number,
    blankForm: boolean = false
  ) => {
    // Parse the group key to extract relevant information
    const groupData: {
      transportType?: number;
      originPort?: number;
      destinationPort?: number;
      bookingType?: number;
      containerSize?: number;
      tariffBracket?: number;
      price?: number | null;
      from?: string;
      to?: string;
    } = parseGroupKey(group.key);

    // Add additional parameters if provided
    if (bookingType) groupData.bookingType = bookingType;
    if (containerSize) groupData.containerSize = containerSize;
    if (tariffBracket) groupData.tariffBracket = tariffBracket;

    // If blankForm is true, we only keep the transport type, origin and destination
    if (blankForm) {
      // Remove additional data, keeping only route information
      delete groupData.price;
      delete groupData.from;
      delete groupData.to;
    } else {
      // Find the most relevant tariff to prefill from
      let referenceTariff: Tariff | undefined;

      // First, try to find a tariff that matches all the specific criteria
      const matchingTariff = findMatchingTariff(
        group.tariffs,
        groupData.transportType,
        groupData.originPort,
        groupData.destinationPort,
        bookingType,
        containerSize,
        tariffBracket
      );

      if (matchingTariff) {
        referenceTariff = matchingTariff;
      } else {
        // If no exact match, find the most recent active tariff
        const activeTariffs = group.tariffs.filter((t) => !isExpiredTariff(t));

        if (activeTariffs.length > 0) {
          // Sort by end date, most recent first
          referenceTariff = [...activeTariffs].sort((a, b) => {
            if (!a.to && !b.to) return 0;
            if (!a.to) return -1;
            if (!b.to) return 1;
            return dayjs(b.to).diff(dayjs(a.to));
          })[0];
        } else if (group.tariffs.length > 0) {
          // If no active tariffs, use the most recent expired one
          referenceTariff = [...group.tariffs].sort((a, b) => {
            if (!a.to && !b.to) return 0;
            if (!a.to) return -1;
            if (!b.to) return 1;
            return dayjs(b.to).diff(dayjs(a.to));
          })[0];
        }
      }

      // Add price and dates from reference tariff if available
      if (referenceTariff) {
        // Copy the price from the reference tariff
        groupData.price = referenceTariff.price;

        // Set start date to today
        // For end date, use the same duration as the reference tariff if possible
        if (referenceTariff.from && referenceTariff.to) {
          const refStartDate = dayjs(referenceTariff.from);
          const refEndDate = dayjs(referenceTariff.to);
          const duration = refEndDate.diff(refStartDate, "day");

          groupData.from = dayjs().toISOString();
          groupData.to = dayjs().add(duration, "day").toISOString();
        }
      }
    }

    setSelectedGroupForAdd(groupData);
    setAddTariffModalVisible(true);
    trackFeatureUsage("tariff_add_from_timeline");
  };

  // Helper function to find matching tariff in a group
  const findMatchingTariff = (
    tariffs: Tariff[],
    transportType?: number,
    originPort?: number,
    destinationPort?: number,
    bookingType?: number,
    containerSize?: number,
    tariffBracket?: number
  ): Tariff | undefined => {
    // First look for exact matches with all criteria
    for (const tariff of tariffs) {
      if (
        (!transportType || tariff.transport_type === transportType) &&
        (!originPort || tariff.origin_port === originPort) &&
        (!destinationPort || tariff.destination_port === destinationPort) &&
        (!bookingType || tariff.bookings_type === bookingType) &&
        (!containerSize || tariff.container_size === containerSize) &&
        (!tariffBracket || tariff.tariff_bracket === tariffBracket)
      ) {
        return tariff;
      }
    }

    // Then look for partial matches
    const partialMatches = tariffs.filter(
      (tariff) =>
        (!transportType || tariff.transport_type === transportType) &&
        (!originPort || tariff.origin_port === originPort) &&
        (!destinationPort || tariff.destination_port === destinationPort)
    );

    if (partialMatches.length > 0) {
      // Sort by recency (most recent end date first)
      return [...partialMatches].sort((a, b) => {
        if (!a.to && !b.to) return 0;
        if (!a.to) return -1;
        if (!b.to) return 1;
        return dayjs(b.to).diff(dayjs(a.to));
      })[0];
    }

    return undefined;
  };

  // Helper function to parse group key
  const parseGroupKey = (groupKey: string) => {
    // Example group key: "Origin → Destination (Transport)"
    const result: {
      transportType?: number;
      originPort?: number;
      destinationPort?: number;
      bookingType?: number;
      containerSize?: number;
      tariffBracket?: number;
      price?: number | null;
      from?: string;
      to?: string;
    } = {};

    // Find matching transport type
    const transportMatch = groupKey.match(/\(([^)]+)\)/);
    if (transportMatch && transportMatch[1]) {
      const transportName = transportMatch[1].trim();
      const matchedTransport = transportTypes.find(
        (t) => t.name === transportName
      );
      if (matchedTransport) {
        result.transportType = matchedTransport.id;
      }
    }

    // Try to extract origin and destination
    const routeParts = groupKey.split("(")[0].split("→");
    if (routeParts.length === 2) {
      const originName = routeParts[0].trim();
      const destName = routeParts[1].trim();

      if (originName !== "Any Origin") {
        const matchedOrigin = originPorts.find(
          (p) =>
            p.name === originName || `${p.name} (${p.country})` === originName
        );
        if (matchedOrigin) {
          result.originPort = matchedOrigin.id;
        }
      }

      if (destName !== "Any Destination") {
        const matchedDest = destinationPorts.find(
          (p) => p.name === destName || `${p.name} (${p.country})` === destName
        );
        if (matchedDest) {
          result.destinationPort = matchedDest.id;
        }
      }
    }

    return result;
  };

  // Handle form submission on add tariff modal
  const handleTariffFormSuccess = () => {
    setAddTariffModalVisible(false);
    message.success("Tariff saved successfully!");
    window.location.reload(); // Consider using a more efficient way to refresh data
  };

  // Toggle expanded/collapsed state for expired tariffs
  const toggleExpiredTariffs = (groupKey: string) => {
    setExpandedExpiredGroups((prevExpanded) => {
      const newExpanded = new Set(prevExpanded);
      if (newExpanded.has(groupKey)) {
        newExpanded.delete(groupKey);
      } else {
        newExpanded.add(groupKey);
      }
      return newExpanded;
    });
    trackFeatureUsage("tariff_expired_toggle");
  };

  // Remove client-side filtering logic since we'll filter on the server
  const filteredTariffs = tariffs;

  // Helper function to check if a tariff is expired
  const isExpiredTariff = (tariff: Tariff): boolean => {
    return getTariffStatus(tariff) === "expired";
  };

  // Group tariffs for visualization
  const groupedTariffs: TariffGroup[] = [];

  // Create route-based groups (origin -> destination)
  const routeGroups = new Map<string, Tariff[]>();

  filteredTariffs.forEach((tariff) => {
    const originName = tariff.origin_port_data?.name || "Any Origin";
    const destName = tariff.destination_port_data?.name || "Any Destination";
    const transportName = tariff.transport_type_data?.name || "Any Transport";

    const groupKey = `${originName} → ${destName} (${transportName})`;

    if (!routeGroups.has(groupKey)) {
      routeGroups.set(groupKey, []);
    }

    routeGroups.get(groupKey)?.push(tariff);
  });

  // Convert the Map to the expected format with sub-grouping
  routeGroups.forEach((groupTariffs, groupName) => {
    // Extract transport type from group name
    const transportType =
      groupName.match(/\(([^)]+)\)/)?.[1] || "Any Transport";
    const transportEmoji = getTransportTypeEmoji(transportType);

    // Update group name to include emoji
    const groupNameWithEmoji = groupName.replace(
      `(${transportType})`,
      `${transportEmoji} (${transportType})`
    );

    // Group by booking type
    const bookingTypeGroups = new Map<string, Tariff[]>();

    groupTariffs.forEach((tariff) => {
      const bookingTypeName = tariff.bookings_type_data?.name || "General";

      if (!bookingTypeGroups.has(bookingTypeName)) {
        bookingTypeGroups.set(bookingTypeName, []);
      }

      bookingTypeGroups.get(bookingTypeName)?.push(tariff);
    });

    // Create the main group
    const mainGroup: TariffGroup = {
      key: groupName,
      label: groupNameWithEmoji,
      tariffs: groupTariffs,
      subGroups: [],
    };

    // Add booking type subgroups
    bookingTypeGroups.forEach((bookingTariffs, bookingTypeName) => {
      // Further group by container size
      const containerSizeGroups = new Map<string, Tariff[]>();
      let bookingTypeId: number | undefined = undefined;

      bookingTariffs.forEach((tariff) => {
        const containerSizeName =
          tariff.container_size_data?.name || "All Sizes";

        // Convert null to undefined for typesafety
        if (tariff.bookings_type !== null) {
          bookingTypeId = tariff.bookings_type;
        }

        if (!containerSizeGroups.has(containerSizeName)) {
          containerSizeGroups.set(containerSizeName, []);
        }

        containerSizeGroups.get(containerSizeName)?.push(tariff);
      });

      // Create booking type subgroup
      const bookingTypeGroup: TariffSubGroup = {
        key: `${mainGroup.key}-${bookingTypeName}`,
        label: bookingTypeName,
        tariffs: bookingTariffs,
        bookingTypeId,
        subGroups: [],
      };

      // Add container size subgroups
      containerSizeGroups.forEach((containerTariffs, containerSizeName) => {
        // Further group by brackets
        const bracketGroups = new Map<string, Tariff[]>();
        let containerSizeId: number | undefined = undefined;

        containerTariffs.forEach((tariff) => {
          const bracketName = tariff.tariff_bracket_data
            ? `${tariff.tariff_bracket_data.from}-${tariff.tariff_bracket_data.to}`
            : "Standard Rate";

          // Convert null to undefined for typesafety
          if (tariff.container_size !== null) {
            containerSizeId = tariff.container_size;
          }

          if (!bracketGroups.has(bracketName)) {
            bracketGroups.set(bracketName, []);
          }

          bracketGroups.get(bracketName)?.push(tariff);
        });

        // Create container size subgroup
        const containerSizeGroup: TariffSubGroup = {
          key: `${bookingTypeGroup.key}-${containerSizeName}`,
          label: containerSizeName,
          tariffs: containerTariffs,
          containerSizeId,
          subGroups: [],
        };

        // Add bracket subgroups
        bracketGroups.forEach((bracketTariffs, bracketName) => {
          let bracketId: number | undefined = undefined;

          if (
            bracketTariffs.length > 0 &&
            bracketTariffs[0].tariff_bracket !== null
          ) {
            bracketId = bracketTariffs[0].tariff_bracket || undefined;
          }

          const bracketGroup: TariffSubGroup = {
            key: `${containerSizeGroup.key}-${bracketName}`,
            label: bracketName,
            tariffs: bracketTariffs,
            bracketId,
            subGroups: [],
          };

          if (containerSizeGroup.subGroups) {
            containerSizeGroup.subGroups.push(bracketGroup);
          }
        });

        if (bookingTypeGroup.subGroups) {
          bookingTypeGroup.subGroups.push(containerSizeGroup);
        }
      });

      if (mainGroup.subGroups) {
        mainGroup.subGroups.push(bookingTypeGroup);
      }
    });

    groupedTariffs.push(mainGroup);
  });

  // Sort groups by name
  groupedTariffs.sort((a, b) => a.label.localeCompare(b.label));

  // Calendar cell renderer
  const dateCellRender = (date: dayjs.Dayjs) => {
    const activeTariffs = filteredTariffs.filter((tariff) => {
      const startDate = tariff.from ? dayjs(tariff.from) : null;
      const endDate = tariff.to ? dayjs(tariff.to) : null;

      if (!startDate && !endDate) return false;

      if (startDate && !endDate) {
        return date.isSame(startDate) || date.isAfter(startDate);
      }

      if (!startDate && endDate) {
        return date.isSame(endDate) || date.isBefore(endDate);
      }

      if (startDate && endDate) {
        return (
          (date.isSame(startDate) || date.isAfter(startDate)) &&
          (date.isSame(endDate) || date.isBefore(endDate))
        );
      }

      return false;
    });

    if (activeTariffs.length === 0) {
      return null;
    }

    // Group by transport type for more meaningful badges
    const transportGroups = new Map<string, Tariff[]>();
    activeTariffs.forEach((tariff) => {
      const transportName = tariff.transport_type_data?.name || "Unknown";
      if (!transportGroups.has(transportName)) {
        transportGroups.set(transportName, []);
      }
      transportGroups.get(transportName)?.push(tariff);
    });

    return (
      <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
        {Array.from(transportGroups.entries()).map(([transport, tariffs]) => (
          <li key={transport}>
            <Tooltip
              title={
                <div>
                  {tariffs.map((tariff) => (
                    <div key={tariff.id}>
                      {generateShortTariffCalendarTitle(tariff)}{" "}
                      {tariff.price
                        ? `€${tariff.price.toFixed(2)}`
                        : "No price"}
                    </div>
                  ))}
                </div>
              }
            >
              <Badge
                status={getTransportTypeColor(transport)}
                text={`${getTransportTypeEmoji(transport)} ${transport} (${
                  tariffs.length
                })`}
                style={{ fontSize: "10px" }}
              />
            </Tooltip>
          </li>
        ))}
      </ul>
    );
  };

  // Render tariffs with expire collapsing
  const renderTariffTimeline = (tariffs: Tariff[], groupKey: string) => {
    // Separate expired from non-expired tariffs
    const expiredTariffs = tariffs.filter(isExpiredTariff);
    const activeTariffs = tariffs.filter((tariff) => !isExpiredTariff(tariff));

    // Check if we need to collapse (more than 3 expired tariffs)
    const shouldCollapse = expiredTariffs.length > 3;
    const isExpanded = expandedExpiredGroups.has(groupKey);

    // Determine which expired tariffs to show
    const visibleExpiredTariffs =
      shouldCollapse && !isExpanded
        ? expiredTariffs.slice(0, 3)
        : expiredTariffs;

    return (
      <>
        {/* Active tariffs are always shown */}
        {activeTariffs.map((tariff) => renderTimelineItem(tariff))}

        {/* Show separator if there are expired tariffs */}
        {expiredTariffs.length > 0 && (
          <div
            style={{
              borderTop: "1px dashed #d9d9d9",
              margin: "12px 0",
              position: "relative",
            }}
          >
            <Text
              type="secondary"
              style={{
                fontSize: "12px",
                position: "absolute",
                top: -10,
                left: 20,
                background: "white",
                padding: "0 8px",
              }}
            >
              Expired Tariffs ({expiredTariffs.length})
            </Text>
          </div>
        )}

        {/* Show visible expired tariffs */}
        {visibleExpiredTariffs.map((tariff) => renderTimelineItem(tariff))}

        {/* Show expand/collapse control if needed */}
        {shouldCollapse && (
          <Button
            type="link"
            onClick={() => toggleExpiredTariffs(groupKey)}
            icon={isExpanded ? <UpOutlined /> : <DownOutlined />}
            style={{ marginLeft: 40 }}
          >
            {isExpanded
              ? "Show fewer"
              : `Show ${expiredTariffs.length - 3} more expired tariffs`}
          </Button>
        )}
      </>
    );
  };

  // Helper function to render a timeline item
  const renderTimelineItem = (tariff: Tariff) => {
    const startDate = tariff.from
      ? dayjs(tariff.from).format("YYYY-MM-DD")
      : "No start date";
    const endDate = tariff.to
      ? dayjs(tariff.to).format("YYYY-MM-DD")
      : "No end date";

    // Get transport type emoji
    const transportType = tariff.transport_type_data?.name || "Any Transport";
    const transportEmoji = getTransportTypeEmoji(transportType);

    // Get tariff bracket information
    const bracketInfo = tariff.tariff_bracket_data
      ? `${tariff.tariff_bracket_data.from} - ${tariff.tariff_bracket_data.to} kg`
      : null;

    return (
      <Timeline.Item
        key={tariff.id}
        label={`${startDate} - ${endDate}`}
        color={getTariffColor(tariff)}
      >
        <div style={{ marginBottom: 4 }}>
          <strong>
            {transportEmoji}{" "}
            {tariff.price !== null
              ? `€${tariff.price.toFixed(2)}`
              : "No price set"}
          </strong>
          <StatusTag tariff={tariff} />
          {bracketInfo && (
            <span style={{ marginLeft: 8, fontSize: "0.85em", color: "#666" }}>
              Weight: {bracketInfo}
            </span>
          )}
        </div>
      </Timeline.Item>
    );
  };

  // Create a form ref to access the form instance
  const formRef = useRef<FormInstance | null>(null);

  // Create a stable callback for setting the form reference with debug output
  const setFormReference = React.useCallback(
    (form: FormInstance) => {
      console.log("Setting form reference:", form);
      formRef.current = form;
    },
    [] // Empty dependency array ensures this callback is stable
  );

  // Memoize initial values to avoid recreating object on each render
  const getInitialValues = React.useCallback(
    (
      groupData: {
        transportType?: number;
        originPort?: number;
        destinationPort?: number;
        bookingType?: number;
        containerSize?: number;
        tariffBracket?: number;
        price?: number | null;
        from?: string;
        to?: string;
      } | null
    ) => {
      if (!groupData) return {};

      return {
        transport_type: groupData.transportType || null,
        origin_port: groupData.originPort || null,
        destination_port: groupData.destinationPort || null,
        bookings_type: groupData.bookingType || null,
        container_size: groupData.containerSize || null,
        tariff_bracket: groupData.tariffBracket || null,
        from: groupData.from ? dayjs(groupData.from) : dayjs(),
        to: groupData.to ? dayjs(groupData.to) : dayjs().add(3, "month"),
        price: groupData.price || null,
      };
    },
    []
  );

  // When opening the add tariff modal, generate suggestions
  const [tariffSuggestions, setTariffSuggestions] = useState<
    Array<{ suggestion: Partial<TariffFormValues>; explanation: string }>
  >([]);

  useEffect(() => {
    if (addTariffModalVisible && selectedGroupForAdd) {
      const suggestions = generateTariffSuggestions(
        tariffs,
        {
          transportType: selectedGroupForAdd.transportType,
          originPort: selectedGroupForAdd.originPort,
          destinationPort: selectedGroupForAdd.destinationPort,
          containerSize: selectedGroupForAdd.containerSize,
          bookingType: selectedGroupForAdd.bookingType,
        },
        transportTypes
      );

      setTariffSuggestions(getTopSuggestions(suggestions, 3));
    } else {
      setTariffSuggestions([]);
    }
  }, [addTariffModalVisible, selectedGroupForAdd, tariffs, transportTypes]);

  // Handle selecting a suggestion
  const handleSelectSuggestion = (suggestion: Partial<TariffFormValues>) => {
    if (formRef.current) {
      try {
        console.log("Applying suggestion to form:", suggestion);

        // Set all fields at once first
        formRef.current.setFieldsValue(suggestion);

        // Then set each field individually to ensure proper updating
        Object.entries(suggestion).forEach(([field, value]) => {
          console.log(`Setting field ${field} to value:`, value);

          // Special handling for dates
          if (field === "from" || field === "to") {
            if (value) {
              console.log(`Setting date field ${field} with dayjs value`);
              formRef.current?.setFieldValue(field, dayjs(value));
            } else {
              console.log(`Setting date field ${field} to null`);
              formRef.current?.setFieldValue(field, null);
            }
          }
          // Special handling for select fields
          else if (
            [
              "transport_type",
              "origin_port",
              "destination_port",
              "container_size",
              "bookings_type",
              "tariff_bracket",
            ].includes(field)
          ) {
            console.log(`Setting select field ${field} to value:`, value);
            formRef.current?.setFieldValue(field, value);

            // If setting transport_type, we need to handle tariff brackets
            if (field === "transport_type" && value) {
              console.log("Setting transport type, triggering bracket update");
              // Instead of trying to dispatch an event, manually filter the tariff brackets
              if (value && formRef.current) {
                // We need to trigger the same effect as handleTransportTypeChange
                // by updating the tariff_bracket field to null when transport_type changes
                formRef.current.setFieldValue("tariff_bracket", null);

                // The component will automatically update availableBrackets in response to the transport_type change
                // No need to do anything else here
              }
            }
          }
          // Default handling for other fields
          else {
            formRef.current?.setFieldValue(field, value);
          }
        });

        trackFeatureUsage("tariff_suggestion_used", {
          suggestionType: suggestion.transport_type,
        });
      } catch (error) {
        console.error("Error applying suggestion to form:", error);
      }
    } else {
      console.warn("Form reference not available when applying suggestion");
    }
  };

  // Update the renderTariffDrawer function to use Modal instead
  const renderTariffModal = () => {
    // Generate initial values only when modal is visible
    const initialValues = getInitialValues(selectedGroupForAdd);

    return (
      <Modal
        title="Add New Tariff"
        open={addTariffModalVisible}
        onCancel={() => {
          // Reset form reference when closing
          formRef.current = null;
          setAddTariffModalVisible(false);
        }}
        maskClosable={true}
        footer={null}
        width={800}
        destroyOnClose
        style={{ top: 20 }}
      >
        {selectedGroupForAdd !== null && (
          <>
            <TariffSuggestions
              suggestions={tariffSuggestions}
              onSelectSuggestion={handleSelectSuggestion}
              transportTypes={transportTypes}
              originPorts={originPorts}
              destinationPorts={destinationPorts}
              containerSizes={containerSizes}
              bookingTypes={bookingTypes || []}
            />
            {/* Use React.memo to prevent unnecessary re-renders */}
            {React.createElement(React.memo(TariffForm), {
              key: `tariff-form-${Date.now()}`,
              mode: "create",
              formRef: setFormReference,
              initialValues: initialValues,
              transportTypes: transportTypes,
              originPorts: originPorts,
              destinationPorts: destinationPorts,
              tariffBrackets: tariffBrackets,
              containerSizes: containerSizes,
              bookingTypes: bookingTypes || [],
              onSuccess: handleTariffFormSuccess,
              onCancel: () => setAddTariffModalVisible(false),
            })}
          </>
        )}
      </Modal>
    );
  };

  // Handler for adding a completely blank tariff
  const handleAddBlankTariff = () => {
    setSelectedGroupForAdd({});
    setAddTariffModalVisible(true);
    trackFeatureUsage("tariff_add_blank");
  };

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: "40px" }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div>
      <div
        style={{
          marginBottom: 16,
          display: "flex",
          flexDirection: "column",
          gap: "16px",
        }}
      >
        <TariffFilterForm
          transportTypes={transportTypes}
          originPorts={originPorts}
          destinationPorts={destinationPorts}
          containerSizes={containerSizes}
          bookingTypes={bookingTypes}
          values={filterValues}
          onChange={handleFilterChange}
          showViewModeToggle={true}
        />
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAddBlankTariff}
          >
            Add Blank Tariff
          </Button>
        </div>
      </div>

      {!filteredTariffs.length ? (
        <Empty description="No tariffs found" style={{ margin: "40px 0" }} />
      ) : filterValues.viewMode === "timeline" ? (
        <div style={{ marginBottom: 40 }}>
          {groupedTariffs.map((group) => (
            <Card
              key={group.key}
              title={group.label}
              style={{
                marginBottom: 24,
                boxShadow: "0 1px 4px rgba(0,0,0,0.1)",
              }}
              extra={
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  size="middle"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleAddTariffFromTimeline(
                      group,
                      undefined,
                      undefined,
                      undefined,
                      true
                    );
                  }}
                >
                  Add Tariff
                </Button>
              }
            >
              <div style={{ marginBottom: 16 }}>
                <Collapse
                  defaultActiveKey={[]}
                  style={{
                    background: "#fff",
                    border: "1px solid #f0f0f0",
                    borderRadius: "4px",
                  }}
                >
                  {group.subGroups &&
                    group.subGroups.map((bookingTypeGroup) => (
                      <Panel
                        header={
                          <Space>
                            <span style={{ fontWeight: 500 }}>
                              {bookingTypeGroup.label} (
                              {bookingTypeGroup.tariffs.length} tariffs)
                            </span>
                            <Button
                              size="small"
                              type="primary"
                              ghost
                              icon={<PlusOutlined />}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleAddTariffFromTimeline(
                                  group,
                                  bookingTypeGroup.bookingTypeId,
                                  undefined,
                                  undefined,
                                  true
                                );
                              }}
                            >
                              Add
                            </Button>
                          </Space>
                        }
                        key={bookingTypeGroup.key}
                        style={{ border: "none", marginBottom: 8 }}
                      >
                        {bookingTypeGroup.label === "General" ? (
                          // Simplified view for unspecified booking types
                          <div>
                            <div style={{ marginBottom: 8 }}>
                              <Text type="secondary">
                                Showing {bookingTypeGroup.tariffs.length}{" "}
                                tariffs with no specific booking type
                              </Text>
                            </div>
                            <Timeline mode="left">
                              {renderTariffTimeline(
                                bookingTypeGroup.tariffs,
                                bookingTypeGroup.key
                              )}
                            </Timeline>
                          </div>
                        ) : bookingTypeGroup.subGroups &&
                          bookingTypeGroup.subGroups.length > 0 ? (
                          <Collapse defaultActiveKey={[]}>
                            {bookingTypeGroup.subGroups.map(
                              (containerSizeGroup) => (
                                <Panel
                                  header={
                                    <Space>
                                      <span>
                                        {containerSizeGroup.label} (
                                        {containerSizeGroup.tariffs.length}{" "}
                                        tariffs)
                                      </span>
                                      <Button
                                        size="small"
                                        type="text"
                                        icon={<PlusOutlined />}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleAddTariffFromTimeline(
                                            group,
                                            bookingTypeGroup.bookingTypeId,
                                            containerSizeGroup.containerSizeId,
                                            undefined,
                                            true
                                          );
                                        }}
                                      >
                                        Add
                                      </Button>
                                    </Space>
                                  }
                                  key={containerSizeGroup.key}
                                >
                                  {containerSizeGroup.label === "All Sizes" ? (
                                    // Simplified view for unspecified container sizes
                                    <div>
                                      <div style={{ marginBottom: 8 }}>
                                        <Text type="secondary">
                                          Showing{" "}
                                          {containerSizeGroup.tariffs.length}{" "}
                                          tariffs with no specific container
                                          size
                                        </Text>
                                      </div>
                                      <Timeline mode="left">
                                        {renderTariffTimeline(
                                          containerSizeGroup.tariffs,
                                          containerSizeGroup.key
                                        )}
                                      </Timeline>
                                    </div>
                                  ) : containerSizeGroup.subGroups &&
                                    containerSizeGroup.subGroups.length > 0 ? (
                                    <div>
                                      {containerSizeGroup.subGroups.map(
                                        (bracketGroup) => (
                                          <div
                                            key={bracketGroup.key}
                                            style={{ marginBottom: 16 }}
                                          >
                                            <div
                                              style={{
                                                display: "flex",
                                                justifyContent: "space-between",
                                                marginBottom: 8,
                                              }}
                                            >
                                              <strong>
                                                Bracket: {bracketGroup.label}{" "}
                                                {bracketGroup.tariffs.length >
                                                  0 &&
                                                  bracketGroup.tariffs[0]
                                                    .transport_type_data
                                                    ?.name &&
                                                  getTransportTypeEmoji(
                                                    bracketGroup.tariffs[0]
                                                      .transport_type_data.name
                                                  )}
                                              </strong>
                                              <Button
                                                size="small"
                                                type="primary"
                                                ghost
                                                icon={<PlusOutlined />}
                                                onClick={() =>
                                                  handleAddTariffFromTimeline(
                                                    group,
                                                    bookingTypeGroup.bookingTypeId,
                                                    containerSizeGroup.containerSizeId,
                                                    bracketGroup.bracketId,
                                                    true
                                                  )
                                                }
                                              >
                                                Add Tariff
                                              </Button>
                                            </div>
                                            <Timeline
                                              mode="left"
                                              style={{ padding: "0 0 16px 0" }}
                                            >
                                              {renderTariffTimeline(
                                                bracketGroup.tariffs,
                                                bracketGroup.key
                                              )}
                                            </Timeline>
                                          </div>
                                        )
                                      )}
                                    </div>
                                  ) : (
                                    <Empty description="No tariffs found" />
                                  )}
                                </Panel>
                              )
                            )}
                          </Collapse>
                        ) : (
                          <Empty description="No tariffs found" />
                        )}
                      </Panel>
                    ))}
                </Collapse>
              </div>

              {/* Footer with explanations */}
              <div
                style={{
                  marginTop: 16,
                  borderTop: "1px solid #f0f0f0",
                  paddingTop: 16,
                  display: "flex",
                  alignItems: "center",
                  flexWrap: "wrap",
                }}
              >
                <Text
                  type="secondary"
                  style={{ display: "flex", alignItems: "center" }}
                >
                  <InfoCircleOutlined style={{ marginRight: 8 }} />
                  Colors indicate tariff status:
                </Text>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    marginLeft: 8,
                    flexWrap: "wrap",
                  }}
                >
                  <Badge
                    status="success"
                    text="Active"
                    style={{ marginRight: 12 }}
                  />
                  <Badge
                    status="warning"
                    text="Expiring Soon"
                    style={{ marginRight: 12 }}
                  />
                  <Badge
                    status="processing"
                    text="Future"
                    style={{ marginRight: 12 }}
                  />
                  <Badge
                    status="default"
                    text="Expired"
                    style={{ marginRight: 12 }}
                  />
                  <Badge status="error" text="Incomplete" />
                </div>
              </div>
            </Card>
          ))}
        </div>
      ) : (
        <div style={{ marginBottom: 40 }}>
          <Card
            title="Tariff Calendar View"
            extra={
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => {
                  setSelectedGroupForAdd({});
                  setAddTariffModalVisible(true);
                }}
              >
                Add Tariff
              </Button>
            }
          >
            <Calendar
              dateCellRender={dateCellRender}
              mode="month"
              style={{ background: "#fff", padding: 16 }}
              fullscreen={true}
              headerRender={({ value, onChange }) => {
                return (
                  <div
                    style={{
                      padding: "8px 0",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <div>
                      <div style={{ fontSize: "16px", fontWeight: "bold" }}>
                        {value.format("MMMM YYYY")}
                      </div>
                      <div style={{ fontSize: "12px", color: "#8c8c8c" }}>
                        Showing {filteredTariffs.length} tariffs
                      </div>
                    </div>
                    <div>
                      <Button
                        size="small"
                        onClick={() => onChange(value.subtract(1, "month"))}
                      >
                        Previous
                      </Button>
                      <Button
                        size="small"
                        style={{ margin: "0 8px" }}
                        onClick={() => onChange(dayjs())}
                      >
                        Today
                      </Button>
                      <Button
                        size="small"
                        onClick={() => onChange(value.add(1, "month"))}
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                );
              }}
            />
          </Card>
        </div>
      )}

      {/* Always render the modal */}
      {renderTariffModal()}
    </div>
  );
};

export default TariffTimelineTab;
